import { DeleteOutlined, EditFilled, PlusOutlined } from '@ant-design/icons';
import { AutoComplete, Button, Modal, Switch, Table, Select } from 'antd';
import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useSelector, useDispatch } from 'react-redux';
import {
    fetchTenderRecords,
    insertTenderRecord,
    updateTenderRecord,
    deleteTenderRecord,
    changeTenderParameter,
} from '../../redux/slice/tender.slice';
import { fetchCountries } from '../../apis/master/country.api';
import moment from 'moment';
import { fetchSector } from '../../apis/master/sector.api';
import { fetchFundingAgencies } from '../../apis/master/funding_agency';
import { fetchRegion } from '../../apis/master/region.api';
import { fetchCPVCode } from '../../apis/master/cpvcode.api';
import { fetchStates } from '../../apis/master/state.api';
import { userRoles } from '../../utils/roles';
import { useCookies } from 'react-cookie';
import DebouncedSearch from '../Common/DebouncedSearch';
import { useNavigate } from 'react-router-dom';
import CommonTable from '../Common/AdvancedTable';
import AdvancedFilter from '../Common/AdvancedFilter';
import { FilterOutlined } from '@ant-design/icons';
import { bulkDeleteTenders } from '../../apis/tender.api';

const inputClass = `form-control
block
w-full
px-3
py-1.5
text-base
font-normal
text-gray-700
bg-white bg-clip-padding
border border-solid border-gray-300
rounded
transition
ease-in-out
m-0
focus:text-gray-700 focus:bg-white focus:border-blue-600 focus:outline-none`;

export default function Tender() {
    const dispatch = useDispatch();
    const [cookies] = useCookies(['role']);
    const { records, loading, pagination } = useSelector((state) => state.tender);
    const { countries, sectors, regions, cpvCodes, fundingAgencies, states } =
        useSelector((state) => state.masterData);

    // Advanced filter state
    const [activeFilters, setActiveFilters] = useState({});

    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [selectedRows, setSelectedRows] = useState([]);

    const getActiveFilterCount = () => {
        return Object.entries(activeFilters).filter(([key, value]) => {
            if (Array.isArray(value)) return value.length > 0;
            if (typeof value === 'boolean') return true;
            return value !== undefined && value !== null && value !== '';
        }).length;
    };

    // Async fetchers for autocomplete
    const fetchCountryOptions = async (keywords) => {
        if (!keywords) return [];
        const res = await fetchCountries({
            pageNo: 0,
            limit: 15,
            sortBy: 1,
            sortField: 'name',
            keywords,
        });
        const data = res.data?.result?.result || [];
        return data.map((s) => ({ label: s?.name, value: s?.name }));
    };
    const fetchSectorOptions = async (keywords) => {
        if (!keywords) return [];
        const res = await fetchSector({
            pageNo: 0,
            limit: 15,
            sortBy: 1,
            sortField: 'name',
            keywords,
        });
        const data = res.data?.result?.result || [];
        return data.map((s) => ({ label: s?.name, value: s?.name }));
    };
    const fetchCPVOptions = async (keywords) => {
        if (!keywords) return [];
        const res = await fetchCPVCode({
            pageNo: 0,
            limit: 15,
            sortBy: 1,
            sortField: 'code',
            keywords,
        });
        const data = res.data?.result?.result || [];
        return data.map((s) => ({ label: s.code, value: s.code }));
    };
    const fetchRegionOptions = async (keywords) => {
        if (!keywords) return [];
        const res = await fetchRegion({
            pageNo: 0,
            limit: 15,
            sortBy: 1,
            sortField: 'name',
            keywords,
        });
        const data = res.data?.result?.result || [];
        return data.map((s) => ({ label: s?.name, value: s?.name }));
    };
    const fetchFundingAgencyOptions = async (keywords) => {
        if (!keywords) return [];
        const res = await fetchFundingAgencies({
            pageNo: 0,
            limit: 15,
            sortBy: 1,
            sortField: 'title',
            keywords,
        });
        const data = res.data?.result?.result || [];
        return data.map((s) => ({ label: s?.title, value: s?.title }));
    };
    const fetchStateOptions = async (keywords) => {
        if (!keywords) return [];
        const res = await fetchStates({
            pageNo: 0,
            limit: 15,
            sortBy: 1,
            sortField: 'name',
            keywords,
        });
        const data = res.data?.result?.result || [];
        return data.map((s) => ({ label: s?.name, value: s?.name }));
    };
    const rowSelection = {
        selectedRowKeys,
        onChange: (keys, rows) => {
            setSelectedRowKeys(keys);
            setSelectedRows(rows);
        },
    };
    // Filter fields config
    const filterFields = [
        { name: 'big_ref_no', label: 'Big Ref No', type: 'text' },
        {
            name: 'tender_type',
            label: 'Tender Type',
            type: 'select',
            options: [
                { label: 'Live', value: 'Live' },
                { label: 'Archive', value: 'Archive' },
            ],
        },
        { name: 'script_name', label: 'Script Name', type: 'text' },
        { name: 'tender_no', label: 'Tender No', type: 'text' },
        { name: 'closing_date', label: 'Closing Date', type: 'daterange' },
        {
            name: 'country',
            label: 'Country',
            type: 'select',
            options: countries,
            mode: 'multiple',
        },
        {
            name: 'state',
            label: 'State',
            type: 'select',
            options: states,
        },
        {
            name: 'cpv_codes',
            label: 'CPV Codes',
            type: 'select',
            options: cpvCodes,
            mode: 'multiple',
        },
        {
            name: 'funding_agency',
            label: 'Funding Agency',
            type: 'select',
            options: fundingAgencies,
            mode: 'multiple',
        },
        {
            name: 'is_active',
            label: 'Is Active',
            type: 'select',
            options: [
                { label: 'Active', value: true },
                { label: 'Inactive', value: false },
            ],
        },
        { name: 'published_date', label: 'Published Date', type: 'daterange' },
        {
            name: 'regions',
            label: 'Regions',
            type: 'select',
            options: regions,
            mode: 'multiple',
        },
        {
            name: 'sectors',
            label: 'Sectors',
            type: 'select',
            options: sectors,
            mode: 'multiple',
        },
    ];

    // Handle filter apply
    const handleAdvancedFilter = (values) => {
        // Convert date ranges to the format expected by backend
        const filters = { ...values };

        // Handle date ranges
        if (filters.closing_date) {
            const startDate = filters.closing_date[0]?.toISOString().split('T')[0];
            const endDate = filters.closing_date[1]?.toISOString().split('T')[0];
            filters.closing_date = `${startDate},${endDate}`;
        }
        if (filters.published_date) {
            const startDate = filters.published_date[0]?.toISOString().split('T')[0];
            const endDate = filters.published_date[1]?.toISOString().split('T')[0];
            filters.posting_date = `${startDate},${endDate}`;
            delete filters.published_date;
        }

        // Handle multi-select arrays - convert to comma-separated strings for backend
        const multiSelectFields = [
            'country',
            'sectors',
            'regions',
            'cpv_codes',
            'funding_agency',
        ];
        multiSelectFields.forEach((field) => {
            if (Array.isArray(filters[field]) && filters[field].length > 0) {
                filters[field] = filters[field].join(',');
            }
        });

        const updatedPagination = { ...pagination, pageNo: 0, ...filters };
        dispatch(changeTenderParameter(updatedPagination));
        dispatch(fetchTenderRecords(updatedPagination));
        setActiveFilters(values); // Save the last applied filters
    };

    // const handleAdvancedReset = () => {

    //     dispatch(changeTenderParameter({ ...pagination, pageNo: 0 }));
    //     dispatch(fetchTenderRecords({ ...pagination, pageNo: 0 }));
    //     setActiveFilters({});
    // };
    const handleAdvancedReset = () => {
        const resetPayload = {
            pageNo: 0,
            limit: pagination.limit,
            sortBy: pagination.sortBy,
            sortField: pagination.sortField,
            tender_type: pagination.tender_type,
            // ❌ NO FILTER FIELDS HERE
        };

        dispatch(changeTenderParameter(resetPayload));
        dispatch(fetchTenderRecords(resetPayload));
        setActiveFilters({});
    };

    useEffect(() => {
        dispatch(fetchTenderRecords(pagination));
    }, []);

    const handleDeleteSlide = (id) => {
        dispatch(deleteTenderRecord({ id }));
        dispatch(fetchTenderRecords(pagination));
    };

    const handleTenderSubmit = async (data, id, setOpen) => {
        try {
            if (id) {
                data._id = id;
                await dispatch(updateTenderRecord(data));
            } else {
                await dispatch(insertTenderRecord(data));
            }

            dispatch(fetchTenderRecords(pagination));
            setOpen(() => false);
        } catch (err) {
            alert(err.message);
        }
    };

    const handleSearch = (keywords) => {
        console.log('Keywords', keywords);
        const updatedPagination = { ...pagination, pageNo: 0, keywords };
        dispatch(changeTenderParameter(updatedPagination));
        dispatch(fetchTenderRecords(updatedPagination));
    };

    const onChangeTable = (paginate, filter, sorter, extra) => {
        const updatedSortField = sorter.field || '_id';
        const updatedSortBy = sorter.order === 'ascend' ? 1 : -1;

        const updatedPagination = {
            ...pagination,
            pageNo: paginate.current - 1,
            limit: paginate.pageSize,
            sortBy: updatedSortBy,
            sortField: updatedSortField,
            keywords: pagination.keywords,
        };

        dispatch(changeTenderParameter(updatedPagination));
        dispatch(fetchTenderRecords(updatedPagination));
    };

    const columns = [
        {
            title: 'Title',
            dataIndex: 'title',
            key: 'title',
            fixed: 'left',
            width: 200,
            height: 100,
            sorter: (a, b) => a.title - b.title,
        },
        {
            title: 'Authority Name',
            dataIndex: 'authority_name',
            key: 'authority_name',
            fixed: 'left',
            width: 170,
            height: 100,
            sorter: (a, b) => a.authority_name - b.authority_name,
        },
        {
            title: 'Script Name',
            dataIndex: 'script_name',
            key: 'script_name',
            fixed: 'left',
            width: 150,
            height: 100,
            sorter: (a, b) => a.script_name - b.script_name,
        },
        {
            title: 'Address',
            dataIndex: 'address',
            key: 'address',
            width: 150,
            sorter: (a, b) => a.address - b.address,
        },
        {
            title: 'Phone',
            dataIndex: 'telephone',
            key: 'telephone',
            width: 150,
            sorter: (a, b) => a.telephone - b.telephone,
        },
        {
            title: 'Fax Number',
            dataIndex: 'fax_number',
            key: 'fax_number',
            width: 150,
            sorter: (a, b) => a.fax_number - b.fax_number,
        },
        {
            title: 'Email',
            dataIndex: 'email',
            key: 'email',
            width: 150,
            sorter: (a, b) => a.email - b.email,
        },
        {
            title: 'Contact Person',
            dataIndex: 'contact_person',
            key: 'contact_person',
            width: 150,
            sorter: (a, b) => a.contact_person - b.contact_person,
        },
        {
            title: 'Big Ref No',
            dataIndex: 'big_ref_no',
            key: 'big_ref_no',
            width: 150,
            sorter: (a, b) => a.big_ref_no - b.big_ref_no,
        },
        {
            title: 'Description',
            dataIndex: 'description',
            key: 'description',
            width: 150,
            sorter: (a, b) => a.description - b.description,
        },
        {
            title: 'Tender Type',
            dataIndex: 'tender_type',
            key: 'tender_type',
            width: 150,
            sorter: (a, b) => a.tender_type - b.tender_type,
        },
        {
            title: 'Tender No',
            dataIndex: 'tender_no',
            key: 'tender_no',
            width: 150,
            sorter: (a, b) => a.tender_no - b.tender_no,
        },
        {
            title: 'Funding Agency',
            dataIndex: 'funding_agency',
            key: 'funding_agency',
            width: 150,
            sorter: (a, b) => a.funding_agency - b.funding_agency,
        },
        {
            title: 'Tender Competition',
            dataIndex: 'tender_competition',
            key: 'tender_competition',
            width: 150,
            sorter: (a, b) => a.tender_competition - b.tender_competition,
        },
        {
            title: 'Publishing Date',
            dataIndex: 'published_date',
            key: 'published_date',
            width: 150,
            render: (e) => moment(e).format('YYYY/MM/DD HH:mm:ss'),
            sorter: (a, b) => a.published_date - b.published_date,
        },
        {
            title: 'Closing Date',
            dataIndex: 'closing_date',
            key: 'closing_date',
            width: 150,
            render: (e) => moment(e).format('YYYY/MM/DD HH:mm:ss'),
            sorter: (a, b) => a.closing_date - b.closing_date,
        },
        {
            title: 'Country',
            dataIndex: 'country',
            key: 'country',
            width: 150,
            sorter: (a, b) => a.country - b.country,
        },
        {
            title: 'Emd',
            dataIndex: 'emd',
            key: 'emd',
            width: 150,
            sorter: (a, b) => a.emd - b.emd,
        },
        {
            title: 'Estimated Cost',
            dataIndex: 'estimated_cost',
            key: 'estimated_cost',
            width: 150,
            sorter: (a, b) => a.estimated_cost - b.estimated_cost,
        },
        {
            title: 'Sectors',
            dataIndex: 'sectors',
            key: 'sectors',
            width: 150,
            sorter: (a, b) => a.sectors - b.sectors,
        },
        {
            title: 'Cpv Code',
            dataIndex: 'cpv_codes',
            key: 'cpv_codes',
            width: 150,
            sorter: (a, b) => a.cpv_codes - b.cpv_codes,
        },
        {
            title: 'Regions',
            dataIndex: 'regions',
            key: 'regions',
            width: 150,
            sorter: (a, b) => a.regions - b.regions,
        },
        {
            title: 'Date',
            dataIndex: 'createdAt',
            key: 'createdAt',
            fixed: 'right',
            width: '150px',
            render: (e, record) => moment(e).format('lll'),
            sorter: (a, b) => a.createdAt - b.createdAt,
        },
    ].concat(
        [userRoles.SUPER_ADMIN, userRoles.CONTENT_DEPARTMENT].includes(cookies.role)
            ? {
                  title: 'Action',
                  dataIndex: 'Action',
                  key: 'Action',
                  width: '150px',
                  fixed: 'right',
                  render: (e, record) => (
                      <div className="flex gap-2" key={record._id}>
                          <ManagerModel
                              key={record._id}
                              id={record._id}
                              submitHandler={handleTenderSubmit}
                              _tender={record}
                          />
                          <Button
                              title="Delete"
                              icon={<DeleteOutlined />}
                              onClick={() => handleDeleteSlide(record._id)}
                          ></Button>
                      </div>
                  ),
              }
            : [],
    );
    const handleBulkDelete = async () => {
        if (!selectedRowKeys.length) return;

        Modal.confirm({
            title: 'Delete selected tenders?',
            content: `Are you sure delete ${selectedRowKeys.length} tenders?`,
            okText: 'Yes Delete',
            okType: 'danger',
            onOk: async () => {
                try {
                    await bulkDeleteTenders(selectedRowKeys);

                    console.log('delete properly');

                    setSelectedRowKeys([]);
                    dispatch(fetchTenderRecords(pagination));
                } catch (err) {
                    // message.error('Delete failed');
                    console.log('Delete failed');
                }
            },
        });
    };

    return (
        <div>
            <div className="flex items-center justify-between mb-4">
                <h1 className="text-xl">Tender</h1>
                <div className="flex gap-2 ml-auto ">
                    <AdvancedFilter
                        fields={filterFields}
                        onApply={handleAdvancedFilter}
                        onReset={handleAdvancedReset}
                        badgeCount={getActiveFilterCount()}
                    />
                    <DebouncedSearch
                        onSearch={handleSearch}
                        placeholder="Search Tenders..."
                    />
                    <ManagerModel submitHandler={handleTenderSubmit} />
                    <Button
                        danger
                        disabled={selectedRowKeys.length === 0}
                        onClick={handleBulkDelete}
                    >
                        Delete Selected ({selectedRowKeys.length})
                    </Button>
                </div>
            </div>
            <CommonTable
                data={records}
                columns={columns}
                loading={loading}
                pagination={pagination}
                onChange={onChangeTable}
                rowSelection={rowSelection}
            />
        </div>
    );
}

function ManagerModel({ id, _tender, submitHandler }) {
    // console.log('Tender000', _tender);

    const [open, setOpen] = useState(false);
    const {
        register,
        handleSubmit,
        formState: { errors },
        setValue,
        control,
    } = useForm();
    const [cookies] = useCookies(['role']);
    const { countries, sectors, regions, cpvCodes, fundingAgencies, states } =
        useSelector((state) => state.masterData);

    const competitionType = ['Domestic ', 'International', 'Both'];
    // const noticeTypeConst = ["Tender", "Project", "Contract Award", "Grants"];
    const tenderTypeConst = ['Live  ', 'Archive'];

    const onSubmit = (body) => {
        // Convert multi-select arrays to comma-separated strings for backend compatibility
        const processedBody = { ...body };

        // Convert arrays to comma-separated strings for fields that might be stored as strings in backend
        if (Array.isArray(body.country)) {
            processedBody.country = body.country.join(',');
        }
        if (Array.isArray(body.sectors)) {
            processedBody.sectors = body.sectors.join(',');
        }
        if (Array.isArray(body.regions)) {
            processedBody.regions = body.regions.join(',');
        }
        if (Array.isArray(body.cpv_codes)) {
            processedBody.cpv_codes = body.cpv_codes.join(',');
        }
        if (Array.isArray(body.funding_agency)) {
            processedBody.funding_agency = body.funding_agency.join(',');
        }
        if (processedBody.published_date) {
            processedBody.published_date = moment(processedBody.published_date).format(
                'YYYY/MM/DD HH:mm:ss',
            );
        }
        if (processedBody.closing_date) {
            processedBody.closing_date = moment(processedBody.closing_date).format(
                'YYYY/MM/DD HH:mm:ss',
            );
        }

        submitHandler(processedBody, id, setOpen);
    };

    return (
        <div className="">
            {[userRoles.SUPER_ADMIN, userRoles.CONTENT_DEPARTMENT].includes(
                cookies.role,
            ) && (
                <Button
                    className={!id ? 'float-right' : ''}
                    title={id ? 'Edit' : 'Add'}
                    icon={id ? <EditFilled /> : <PlusOutlined />}
                    onClick={() => setOpen(true)}
                >
                    {id ? '' : 'Add'}
                </Button>
            )}

            <Modal
                centered
                width={1000}
                title={(id ? 'Edit' : 'Add') + ' Tender'}
                open={open}
                onOk={() => setOpen(false)}
                footer={false}
                onCancel={() => setOpen(false)}
            >
                <div>
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 md:gap-3">
                            <div className="form-group mb-6">
                                <label className="font-bold">Title</label>
                                <span className="text-red-600 md:ml-4">
                                    {errors?.title?.message}
                                </span>
                                <input
                                    type="text"
                                    className={inputClass}
                                    name="address"
                                    {...register('title')}
                                    aria-describedby="title"
                                    placeholder="title"
                                    defaultValue={_tender?.title || ''}
                                />
                            </div>
                            <div className="form-group mb-6">
                                <label className="font-bold">
                                    Authority Name <span className="text-red-600">*</span>
                                </label>
                                <span className="text-red-600 md:ml-4">
                                    {errors?.authority_name?.message}
                                </span>
                                <input
                                    type="text"
                                    className={inputClass}
                                    name="authority_name"
                                    {...register('authority_name', {
                                        required: 'field is required',
                                    })}
                                    aria-describedby="authority_name"
                                    placeholder="authority_name"
                                    defaultValue={_tender?.authority_name || ''}
                                />
                            </div>
                            <div className="form-group mb-6">
                                <label className="font-bold">Address</label>
                                <span className="text-red-600 md:ml-4">
                                    {errors?.address?.message}
                                </span>
                                <input
                                    type="text"
                                    className={inputClass}
                                    name="address"
                                    {...register('address')}
                                    aria-describedby="address"
                                    placeholder="address"
                                    defaultValue={_tender?.address || ''}
                                />
                            </div>
                            <div className="form-group mb-6">
                                <label className="font-bold">Phone</label>
                                <span className="text-red-600 md:ml-4">
                                    {errors?.telephone?.message}
                                </span>
                                <input
                                    type="text"
                                    className={inputClass}
                                    name="telephone"
                                    {...register('telephone')}
                                    aria-describedby="telephone"
                                    placeholder="telephone"
                                    defaultValue={_tender?.telephone || ''}
                                />
                            </div>
                            <div className="form-group mb-6">
                                <label className="font-bold">Fax Number</label>
                                <span className="text-red-600 md:ml-4">
                                    {errors?.fax_number?.message}
                                </span>
                                <input
                                    type="text"
                                    className={inputClass}
                                    name="fax_number"
                                    {...register('fax_number')}
                                    aria-describedby="fax_number"
                                    placeholder="fax_number"
                                    defaultValue={_tender?.fax_number || ''}
                                />
                            </div>
                            <div className="form-group mb-6">
                                <label className="font-bold">Email</label>
                                <span className="text-red-600 md:ml-4">
                                    {errors?.email?.message}
                                </span>
                                <input
                                    type="email"
                                    className={inputClass}
                                    name="email"
                                    {...register('email')}
                                    aria-describedby="email"
                                    placeholder="email"
                                    defaultValue={_tender?.email || ''}
                                />
                            </div>
                            <div className="form-group mb-6">
                                <label className="font-bold">Contact Person</label>
                                <span className="text-red-600 md:ml-4">
                                    {errors?.contact_person?.message}
                                </span>
                                <input
                                    type="text"
                                    className={inputClass}
                                    name="contact_person"
                                    {...register('contact_person')}
                                    aria-describedby="contact_person"
                                    placeholder="contact_person"
                                    defaultValue={_tender?.contact_person || ''}
                                />
                            </div>
                        </div>
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 md:gap-3">
                            <div className="form-group mb-6">
                                <label className="font-bold">
                                    Big Ref Number <span className="text-red-600">*</span>
                                </label>
                                <span className="text-red-600 md:ml-4">
                                    {errors?.big_ref_no?.message}
                                </span>
                                <input
                                    type="text"
                                    className={inputClass}
                                    name="big_ref_no"
                                    {...register('big_ref_no', {
                                        required: 'field is required',
                                    })}
                                    aria-describedby="big_ref_no"
                                    placeholder="big_ref_no"
                                    defaultValue={_tender?.big_ref_no || ''}
                                />
                            </div>
                            <div className="form-group mb-6">
                                <label className="font-bold">Description</label>
                                <span className="text-red-600 md:ml-4">
                                    {errors?.description?.message}
                                </span>
                                <input
                                    type="text"
                                    className={inputClass}
                                    name="description"
                                    {...register('description')}
                                    aria-describedby="description"
                                    placeholder="description"
                                    defaultValue={_tender?.description || ''}
                                />
                            </div>
                            <div className="form-group mb-6">
                                <label className="font-bold">Tender Type</label>
                                <span className="text-red-600 md:ml-4">
                                    {errors?.tender_type?.message}
                                </span>
                                <select
                                    type="text"
                                    className={inputClass}
                                    name="tender_type"
                                    {...register('tender_type')}
                                    aria-describedby="tender_type"
                                    placeholder="tender_type"
                                    defaultValue={_tender?.tender_type || ''}
                                >
                                    {tenderTypeConst.map((c) => (
                                        <option key={c} value={c}>
                                            {c}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="form-group mb-6">
                                <label className="font-bold">Tender No</label>
                                <span className="text-red-600 md:ml-4">
                                    {errors?.tender_no?.message}
                                </span>
                                <input
                                    type="text"
                                    className={inputClass}
                                    name="tender_no"
                                    {...register('tender_no')}
                                    aria-describedby="tender_no"
                                    placeholder="tender_no"
                                    defaultValue={_tender?.tender_no || ''}
                                />
                            </div>
                            <div className="form-group mb-6">
                                <label className="font-bold">Funding Agency</label>
                                <span className="text-red-600 md:ml-4">
                                    {errors?.funding_agency?.message}
                                </span>
                                <Select
                                    {...register('funding_agency')}
                                    className={'w-full'}
                                    placeholder="Select Funding Agency"
                                    options={fundingAgencies}
                                    mode="multiple"
                                    defaultValue={
                                        _tender?.funding_agency
                                            ? Array.isArray(_tender.funding_agency)
                                                ? _tender.funding_agency
                                                : _tender.funding_agency
                                                      .split(',')
                                                      .map((s) => s.trim())
                                                      .filter(Boolean)
                                            : []
                                    }
                                    onChange={(value) =>
                                        setValue('funding_agency', value)
                                    }
                                />
                            </div>
                            <div className="form-group mb-6">
                                <label className="font-bold">Tender Compition</label>
                                <span className="text-red-600 md:ml-4">
                                    {errors?.tender_competition?.message}
                                </span>
                                <select
                                    type="text"
                                    className={inputClass}
                                    name="tender_competition"
                                    {...register('tender_competition')}
                                    aria-describedby="tender_competition"
                                    placeholder="tender_competition"
                                    defaultValue={_tender?.tender_competition || ''}
                                >
                                    {competitionType.map((c) => (
                                        <option key={c} value={c}>
                                            {c}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 md:gap-3">
                            <div className="form-group mb-6">
                                <label className="font-bold">Publishing Date</label>
                                <span className="text-red-600 md:ml-4">
                                    {errors?.published_date?.message}
                                </span>
                                <input
                                    type="date"
                                    className={inputClass}
                                    name="published_date"
                                    {...register('published_date')}
                                    aria-describedby="published_date"
                                    placeholder="published_date"
                                    defaultValue={
                                        moment(_tender?.published_date).format(
                                            'YYYY-MM-DD',
                                        ) || ''
                                    }
                                />
                            </div>
                            <div className="form-group mb-6">
                                <label className="font-bold">Closing Date</label>
                                <span className="text-red-600 md:ml-4">
                                    {errors?.closing_date?.message}
                                </span>
                                <input
                                    type="date"
                                    className={inputClass}
                                    name="closing_date"
                                    {...register('closing_date')}
                                    aria-describedby="closing_date"
                                    placeholder="closing_date"
                                    defaultValue={
                                        moment(_tender?.closing_date).format(
                                            'YYYY-MM-DD',
                                        ) || ''
                                    }
                                />
                            </div>
                            <div className="form-group mb-6">
                                <label className="font-bold">Country</label>
                                <span className="text-red-600 md:ml-4">
                                    {errors?.country?.message}
                                </span>
                                <Select
                                    {...register('country')}
                                    className={'w-full'}
                                    placeholder="Select Country"
                                    options={countries}
                                    mode="multiple"
                                    defaultValue={
                                        _tender?.country
                                            ? Array.isArray(_tender.country)
                                                ? _tender.country
                                                : _tender.country
                                                      .split(',')
                                                      .map((s) => s.trim())
                                                      .filter(Boolean)
                                            : []
                                    }
                                    onChange={(value) => setValue('country', value)}
                                />
                            </div>
                            <div className="form-group mb-6">
                                <label className="font-bold">EMD</label>
                                <span className="text-red-600 md:ml-4">
                                    {errors?.emd?.message}
                                </span>
                                <input
                                    type="text"
                                    className={inputClass}
                                    name="emd"
                                    {...register('emd')}
                                    aria-describedby="emd"
                                    placeholder="emd"
                                    defaultValue={_tender?.emd || ''}
                                />
                            </div>
                            <div className="form-group mb-6">
                                <label className="font-bold">Estimated Cost</label>
                                <span className="text-red-600 md:ml-4">
                                    {errors?.estimated_cost?.message}
                                </span>
                                <input
                                    type="text"
                                    className={inputClass}
                                    name="estimated_cost"
                                    {...register('estimated_cost')}
                                    aria-describedby="estimated_cost"
                                    placeholder="estimated_cost"
                                    defaultValue={_tender?.estimated_cost || ''}
                                />
                            </div>
                            <div className="form-group mb-6">
                                <label className="font-bold">Documents</label>
                                <span className="text-red-600 md:ml-4">
                                    {errors?.documents?.message}
                                </span>
                                <input
                                    type="text"
                                    className={inputClass}
                                    name="documents"
                                    {...register('documents')}
                                    aria-describedby="documents"
                                    placeholder="documents"
                                    defaultValue={_tender?.documents || ''}
                                />
                            </div>
                        </div>
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 md:gap-3">
                            <div className="form-group mb-6">
                                <label className="font-bold">
                                    Sector <span className="text-red-600">*</span>
                                </label>
                                <span className="text-red-600 md:ml-4">
                                    {errors?.sectors?.message}
                                </span>
                                <Controller
                                    name="sectors"
                                    control={control}
                                    rules={{
                                        validate: (value) =>
                                            value?.length > 0 || 'Field is required',
                                    }}
                                    defaultValue={
                                        _tender?.sectors
                                            ? Array.isArray(_tender.sectors)
                                                ? _tender.sectors
                                                : _tender.sectors
                                                      .split(',')
                                                      .map((s) => s.trim())
                                                      .filter(Boolean)
                                            : []
                                    }
                                    render={({ field }) => (
                                        <Select
                                            {...field}
                                            className="w-full"
                                            placeholder="Select Sector"
                                            options={sectors}
                                            mode="multiple"
                                        />
                                    )}
                                />
                            </div>
                            <div className="form-group mb-6">
                                <label className="font-bold">
                                    Region <span className="text-red-600">*</span>
                                </label>
                                <span className="text-red-600 md:ml-4">
                                    {errors?.regions?.message}
                                </span>
                                <Controller
                                    name="regions"
                                    control={control}
                                    rules={{
                                        validate: (value) =>
                                            value?.length > 0 || 'Field is required',
                                    }}
                                    defaultValue={
                                        _tender?.regions
                                            ? Array.isArray(_tender.regions)
                                                ? _tender.regions
                                                : _tender.regions
                                                      .split(',')
                                                      .map((s) => s.trim())
                                                      .filter(Boolean)
                                            : []
                                    }
                                    render={({ field }) => (
                                        <Select
                                            {...field}
                                            className="w-full"
                                            placeholder="Select Region"
                                            options={regions}
                                            mode="multiple"
                                        />
                                    )}
                                />
                            </div>
                            <div className="form-group mb-6">
                                <label className="font-bold">
                                    CPV Code <span className="text-red-600">*</span>
                                </label>
                                <span className="text-red-600 md:ml-4">
                                    {errors?.cpv_codes?.message}
                                </span>
                                <Controller
                                    name="cpv_codes"
                                    control={control}
                                    rules={{
                                        validate: (value) =>
                                            value?.length > 0 || 'Field is required',
                                    }}
                                    defaultValue={
                                        _tender?.cpv_codes
                                            ? Array.isArray(_tender.cpv_codes)
                                                ? _tender.cpv_codes
                                                : _tender.cpv_codes
                                                      .split(',')
                                                      .map((s) => s.trim())
                                                      .filter(Boolean)
                                            : []
                                    }
                                    render={({ field }) => (
                                        <Select
                                            {...field}
                                            className="w-full"
                                            placeholder="Select CPV Code"
                                            options={cpvCodes}
                                            mode="multiple"
                                        />
                                    )}
                                />
                            </div>
                        </div>
                        <div>
                            <button
                                type="submit"
                                className="px-4 py-2 rounded shadow hover:bg-gray-200"
                            >
                                Submit
                            </button>
                        </div>
                    </form>
                </div>
            </Modal>
        </div>
    );
}
